const ICON_URL = '/img/icons/custom-group.svg';
// Two em-spaces (~1em each) reserve room before the label: the first becomes the
// gap between the colour swatch and the grey pill; the second is overlaid by the
// icon (inside the pill). The trailing regular space gaps the icon from the text.
const LEGEND_TEXT_PREFIX = '   ';

let cachedImage = null;
const pendingLoadCallbacks = new Set();

function ensureIcon() {
  if (typeof Image === 'undefined') return null;
  if (cachedImage) return cachedImage;
  cachedImage = new Image();
  cachedImage.addEventListener('load', () => {
    const callbacks = Array.from(pendingLoadCallbacks);
    pendingLoadCallbacks.clear();
    callbacks.forEach((cb) => {
      try {
        cb();
      } catch (e) {
        // chart may have been destroyed
      }
    });
  });
  cachedImage.src = ICON_URL;
  return cachedImage;
}

ensureIcon();

function iconIsReady(icon) {
  return icon && icon.complete && icon.naturalWidth > 0;
}

export function withCustomGroupPrefix(text, isCustom) {
  return isCustom ? `${LEGEND_TEXT_PREFIX}${text}` : text;
}

// Grey rounded "pill" behind custom-group legend entries — matches the title
// styling in `.location-name.custom-group-name` (_type.scss): #e4e4e4, 5px radius.
const BOX_FILL = '#e4e4e4';
const BOX_RADIUS = 5;
const BOX_PAD_X = 10; // right padding, after the label text
const BOX_PAD_Y = 6;
// Gap between the colour swatch and the pill's left edge. The label's first
// reserved em-space spans this region; we start the pill a few px into it so a
// round swatch never touches the grey background.
const SWATCH_GAP = 8;

// Manual rounded-rect path (avoids relying on ctx.roundRect for cross-browser safety).
function roundRect(ctx, x, y, width, height, radius) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
}

const customGroupLegendIconPlugin = {
  id: 'customGroupLegendIcon',
  afterDraw(chart) {
    const legend = chart.legend;
    if (!legend || !legend.legendItems || !legend.legendHitBoxes) return;

    const flagged = legend.legendItems.map((item, i) => ({ item, hitBox: legend.legendHitBoxes[i] })).filter(({ item, hitBox }) => item && hitBox && item.isCustomGroup);

    if (flagged.length === 0) return;

    const icon = ensureIcon();
    if (!iconIsReady(icon)) {
      pendingLoadCallbacks.add(() => {
        try {
          chart.update('none');
        } catch (e) {
          // chart may have been destroyed
        }
      });
      return;
    }

    const fontSize = legend.options?.labels?.font?.size || 12;
    const iconSize = fontSize;
    const ctx = chart.ctx;

    // Legend font size, swatch shape ('circle' vs 'line') and the swatch-to-text
    // gap all differ between chart types, so the label does NOT begin at a fixed
    // offset. Find where it actually starts by measuring the rendered text:
    //   labelStart = hitBox.left + (hitBox.width - measuredTextWidth)
    // This keeps the pill identical across every chart. An em-space is 1em (==
    // fontSize) wide; the label carries two of them — the first becomes the gap
    // between the swatch and the pill, the second is overlaid by the icon.
    const fontFamily = (typeof getComputedStyle !== 'undefined' && document.body && getComputedStyle(document.body).getPropertyValue('--base-font')) || 'sans-serif';

    ctx.save();
    ctx.font = `${fontSize}px ${fontFamily}`;
    const geometry = flagged.map(({ item, hitBox }) => {
      const textWidth = ctx.measureText(item.text).width;
      const labelStart = hitBox.left + Math.max(0, hitBox.width - textWidth);
      // Pill starts SWATCH_GAP into the label so the swatch keeps clear space;
      // the icon overlays the second reserved em-space, just inside the pill.
      return { hitBox, iconX: labelStart + fontSize, boxLeft: labelStart + SWATCH_GAP };
    });
    ctx.restore();

    // Draw the grey pill behind the icon + label only (swatch excluded).
    // `destination-over` composites the fill beneath the icon/text Chart.js already
    // painted, so the text stays crisp and we never re-render it.
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = BOX_FILL;
    geometry.forEach(({ hitBox, boxLeft }) => {
      const right = hitBox.left + hitBox.width + BOX_PAD_X;
      roundRect(ctx, boxLeft, hitBox.top - BOX_PAD_Y, right - boxLeft, hitBox.height + BOX_PAD_Y * 2, BOX_RADIUS);
      ctx.fill();
    });
    ctx.restore();

    ctx.save();
    geometry.forEach(({ hitBox, iconX }) => {
      const y = hitBox.top + (hitBox.height - iconSize) / 2;
      ctx.drawImage(icon, iconX, y, iconSize, iconSize);
    });
    ctx.restore();
  },
};

export default customGroupLegendIconPlugin;
